import { storeToRefs } from 'pinia'
import { realtimeDatabaseService } from '@/services/realtimeDBService'
import { useCommonStore } from '@/stores/CommonStore'

export interface UseFirebaseSessionReturn {
  initializeFirebaseAuth: () => Promise<boolean>
  refreshFirebaseAuth: () => Promise<boolean>
}

/**
 * Wraps the common pattern of issuing a Firebase custom token from the
 * backend and then authenticating (or reauthenticating) with Firebase RTDB.
 *
 * - initializeFirebaseAuth: issues token + authenticateUser (no-op if already signed in)
 * - refreshFirebaseAuth: issues token + reauthenticateUser (sign-out then sign-in
 *   so updated claims like caseId take effect)
 */
export function useFirebaseSession(): UseFirebaseSessionReturn {
  const commonStore = useCommonStore()
  const { fetchFirebaseAuthToken } = commonStore
  const { fbAuthToken } = storeToRefs(commonStore)

  async function initializeFirebaseAuth(): Promise<boolean> {
    await fetchFirebaseAuthToken()

    if (!fbAuthToken.value) return false

    return await realtimeDatabaseService.authenticateUser(fbAuthToken.value)
  }

  async function refreshFirebaseAuth(): Promise<boolean> {
    await fetchFirebaseAuthToken()

    if (!fbAuthToken.value) return false

    return await realtimeDatabaseService.reauthenticateUser(fbAuthToken.value)
  }

  return { initializeFirebaseAuth, refreshFirebaseAuth }
}
